import { LanguagePreference } from 'models/LanguagePreference'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getFormattedAddress } from 'redux/reducers/users'
import { useMedicareUrl } from './useMedicareUrl'

const cervicalAndVaginalCancerScreeningTranslations = {
  en: 'Cervical and vaginal cancer screening',
  es: 'Detección de cáncer de cuello uterino y de vagina'
}

const prostateCancerScreeningTranslations = {
  en: 'Prostate cancer screening',
  es: 'Detección de cáncer de próstata'
}

const preventiveServicesMap = {
  PHYSICAL: {
    en: 'Welcome to Medicare visit',
    es: "Consulta 'Bienvenido a Medicare'"
  },
  'ANNUAL WELLNESS VISIT': {
    en: 'Annual wellness visit',
    es: 'Visita anual de bienestar'
  },
  DIABETES: {
    en: 'Diabetes screening',
    es: 'Detección de diabetes'
  },
  'PAP TEST': cervicalAndVaginalCancerScreeningTranslations,
  'PAP TEST DR': cervicalAndVaginalCancerScreeningTranslations,
  PELVIC: cervicalAndVaginalCancerScreeningTranslations,
  PROSTATE: prostateCancerScreeningTranslations,
  PSA: prostateCancerScreeningTranslations,
  PPV: {
    en: 'Pneumococcal shot',
    es: 'Vacuna antineumocócica'
  },
  MAMMOGRAPHY: {
    en: 'Mammogram',
    es: 'Mamograma'
  },
  'ABDOMINAL AORTIC ANEURYSM': {
    en: 'Abdominal aortic aneurysm screening',
    es: 'Examen de aneurisma aórtico abdominal'
  },
  COLORECTAL: {
    en: 'Colorectal cancer screening',
    es: 'Examen de cáncer colorrectal'
  },
  'SMOKING CESSATION': {
    en: 'Tobacco use cessation counseling',
    es: 'Consumo de tabaco y asesoramiento para dejar de fumar'
  },
  CARDIOVASCULAR: {
    en: 'Cardiovascular disease screening',
    es: 'Detección de enfermedades cardiovasculares'
  }
}

const isKeyOfPreventiveServicesMap = (value: string | undefined): value is keyof typeof preventiveServicesMap => {
  return value !== undefined && Object.keys(preventiveServicesMap).includes(value)
}

const defaultProviderUrlSearchTerm = {
  en: 'Primary care provider',
  es: 'Proveedor de atención primaria'
}

/**
 * TODO: In the future this will take an optional additional field and this hook will include logic to add additional query fields. This will be used on preventive service cards.
 *
 * @returns a url for finding providers based on current language of the page and the user's zip code
 */
export const useFindProviderUrl = (serviceType?: string) => {
  const { i18n } = useTranslation()
  const lang = i18n.language as LanguagePreference
  const providerUrlBase = useMedicareUrl(
    'https://www.medicare.gov/care-compare',
    'https://es.medicare.gov/care-compare'
  )
  const address = useSelector(getFormattedAddress)

  const urls = useMemo(() => {
    const serviceTypeSearchTerm: string | undefined = isKeyOfPreventiveServicesMap(serviceType) ? preventiveServicesMap[serviceType][lang] : undefined

    const url = new URL(providerUrlBase)
    url.searchParams.append('providerType', 'Physician')
    url.searchParams.append('searchTerm', serviceTypeSearchTerm ?? defaultProviderUrlSearchTerm[lang])
    // We don't really expect this to happen because the user should be loaded before getting here, but handling it just in case.
    if (address) {
      url.searchParams.append('userLocation', address)
    }

    const withoutAddress = new URL(url.toString())
    withoutAddress.searchParams.delete('userLocation')

    return {
      findProviderUrl: url.toString(),
      findProviderUrlWithoutAddress: withoutAddress.toString()
    }
  }, [providerUrlBase, address, serviceType, lang])

  return urls
}
